import { useState, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button, Card, CardHeader, CardTitle, CardContent } from '@design-system';
import { Download, ChevronRight, X, BarChart3 } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { SortHeader, renderHeaderCell } from '@/components/DataTableColumn';
import { useSearchParams } from 'react-router-dom';

interface SubscriptionDetailsTableProps {
  getFilteredSubscriptions: (filterKey?: string | null) => AdvisorSubscriptionMetrics[];
  isLoading: boolean;
  filterKey?: string | null;
}

const subscriptionColumnHelper = createColumnHelper<AdvisorSubscriptionMetrics>();
const seatColumnHelper = createColumnHelper<AdvisorMetricSubscriptionSeats>();

export function SubscriptionDetailsTable({
  getFilteredSubscriptions,
  isLoading,
  filterKey,
}: SubscriptionDetailsTableProps) {
  const [searchText, setSearchText] = useState('');
  const [selectedSubscription, setSelectedSubscription] =
    useState<AdvisorSubscriptionMetrics | null>(null);
  const [seatSearchText, setSeatSearchText] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const clearFilter = () => {
    setSearchParams({ tab: 'subscriptions' });
  };

  const checkUsage = () => {
    setSearchParams({ tab: 'usage', usageFilter: filterKey || 'all' });
  };

  const filteredSubscriptions = useMemo(() => {
    return getFilteredSubscriptions(filterKey);
  }, [filterKey, getFilteredSubscriptions]);

  const exportSubscriptionsToCsv = () => {
    const headers = [
      'Owner Advisor',
      'Status',
      'Total Seats',
      'Utilized Seats',
      'Billing Interval',
      'Created Date',
      'Next Billing Date',
      'Cancellation Triggered',
      'Cancellation Triggered At',
      'Cancellation Reason',
      'Additional Feedback',
    ];
    const rows = filteredSubscriptions.map((subscription: any) => {
      const utilizedSeats =
        subscription.seats?.filter((seat: any) => seat.status === 'active').length || 0;
      return [
        subscription.ownerAdvisorName || subscription.ownerAdvisorId,
        subscription.subscriptionStatus,
        subscription.totalSeats,
        utilizedSeats,
        subscription.billingInterval,
        new Date(subscription.createdDateTime).toLocaleDateString(),
        subscription.nextBillingDateTime
          ? new Date(subscription.nextBillingDateTime).toLocaleDateString()
          : 'N/A',
        subscription.cancellationTriggered || 'No',
        subscription.cancellationTriggeredAt || 'N/A',
        subscription.cancellationTriggeredReason || 'N/A',
        subscription.additionalFeedback || 'N/A',
      ];
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportSeatsToCsv = () => {
    if (!selectedSubscription?.seats) return;

    const headers = ['Email', 'Advisor ID', 'Status', 'Created Date', 'Updated Date'];
    const rows = selectedSubscription.seats.map(seat => [
      seat.invitedEmail,
      seat.advisorId,
      seat.status,
      new Date(seat.createdDateTime).toLocaleDateString(),
      new Date(seat.updatedDateTime).toLocaleDateString(),
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscription-seats-${selectedSubscription.ownerAdvisorId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusBadgeVariant = (
    status: string,
  ): 'default' | 'destructive' | 'secondary' | 'outline' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'canceled':
      case 'cancelled':
        return 'destructive';
      case 'past_due':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getSeatStatusBadgeVariant = (
    status: string,
  ): 'default' | 'destructive' | 'secondary' | 'outline' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'invited':
        return 'secondary';
      case 'inactive':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const subscriptionColumnDefinitions = [
    subscriptionColumnHelper.display({
      id: 'expand',
      header: () => renderHeaderCell(''),
      cell: info => {
        const row = info.row.original;
        const hasSeats = row.seats && row.seats.length > 0;
        const isSelected = selectedSubscription?.ownerAdvisorId === row.ownerAdvisorId;

        return hasSeats ? (
          <button
            onClick={e => {
              e.stopPropagation();
              setSelectedSubscription(isSelected ? null : row);
            }}
            className="rounded p-1 hover:bg-gray-100"
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform ${isSelected ? 'rotate-90' : ''}`}
            />
          </button>
        ) : null;
      },
      size: 50,
    }),
    subscriptionColumnHelper.accessor((row: any) => row.ownerAdvisorName || row.ownerAdvisorId, {
      id: 'ownerAdvisor',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Owner Advisor
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        const isSelected = selectedSubscription?.ownerAdvisorId === row.ownerAdvisorId;
        return (
          <span className={`font-medium ${isSelected ? 'text-blue-600' : ''}`}>
            {info.getValue()}
          </span>
        );
      },
      enableSorting: true,
      meta: { label: 'Owner Advisor' },
    }),
    subscriptionColumnHelper.accessor('subscriptionStatus', {
      id: 'status',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Status
          </SortHeader>,
        ),
      cell: info => (
        <Badge variant={getStatusBadgeVariant(info.getValue())}>{info.getValue()}</Badge>
      ),
      enableSorting: true,
      meta: { label: 'Status' },
    }),
    subscriptionColumnHelper.accessor('totalSeats', {
      id: 'totalSeats',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Total Seats
          </SortHeader>,
        ),
      cell: info => <span className="font-medium">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Total Seats' },
    }),
    subscriptionColumnHelper.accessor(
      row => row.seats?.filter(seat => seat.status === 'active').length || 0,
      {
        id: 'utilizedSeats',
        header: ({ column }) =>
          renderHeaderCell(
            <SortHeader
              column={column}
              variant="ghost"
            >
              Utilized Seats
            </SortHeader>,
          ),
        cell: info => {
          const row = info.row.original;
          const utilized = info.getValue();
          const total = row.totalSeats;
          const percentage = total > 0 ? Math.round((utilized / total) * 100) : 0;
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {utilized} / {total}
              </span>
              <span className="text-xs text-gray-500">({percentage}%)</span>
            </div>
          );
        },
        enableSorting: true,
        meta: { label: 'Utilized Seats' },
      },
    ),
    subscriptionColumnHelper.accessor('billingInterval', {
      id: 'billingInterval',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Billing Interval
          </SortHeader>,
        ),
      cell: info => <span className="text-sm capitalize text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Billing Interval' },
    }),
    subscriptionColumnHelper.accessor('createdDateTime', {
      id: 'createdDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Created Date
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return <span className="text-sm">{new Date(value).toLocaleDateString()}</span>;
      },
      enableSorting: true,
      meta: { label: 'Created Date' },
    }),
    subscriptionColumnHelper.accessor('nextBillingDateTime', {
      id: 'nextBilling',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Next Billing
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return (
          <span className="text-sm">{value ? new Date(value).toLocaleDateString() : 'N/A'}</span>
        );
      },
      enableSorting: true,
      meta: { label: 'Next Billing' },
    }),
    subscriptionColumnHelper.accessor((row: any) => row.cancellationTriggered || 'No', {
      id: 'cancellationTriggered',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Cancellation Triggered
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return <Badge variant={value === 'Yes' ? 'destructive' : 'secondary'}>{value}</Badge>;
      },
      enableSorting: true,
      meta: { label: 'Cancellation Triggered' },
    }),
    subscriptionColumnHelper.accessor((row: any) => row.cancellationTriggeredAt || 'N/A', {
      id: 'cancellationTriggeredAt',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Cancellation At
          </SortHeader>,
        ),
      cell: info => <span className="text-sm">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Cancellation At' },
    }),
    subscriptionColumnHelper.accessor((row: any) => row.cancellationTriggeredReason || 'N/A', {
      id: 'cancellationReason',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Cancellation Reason
          </SortHeader>,
        ),
      cell: info => <span className="text-sm">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Cancellation Reason' },
    }),
    subscriptionColumnHelper.accessor((row: any) => row.additionalFeedback || 'N/A', {
      id: 'additionalFeedback',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Additional Feedback
          </SortHeader>,
        ),
      cell: info => <span className="text-sm">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Additional Feedback' },
    }),
  ];

  const seatColumnDefinitions = [
    seatColumnHelper.accessor('invitedEmail', {
      id: 'email',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Email
          </SortHeader>,
        ),
      cell: info => {
        const seat: any = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{info.getValue()}</span>
            {seat.isOwner && (
              <Badge
                variant="outline"
                className="text-xs"
              >
                Owner
              </Badge>
            )}
          </div>
        );
      },
      enableSorting: true,
      meta: { label: 'Email' },
    }),
    seatColumnHelper.accessor('advisorId', {
      id: 'advisorId',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Advisor ID
          </SortHeader>,
        ),
      cell: info => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Advisor ID' },
    }),
    seatColumnHelper.accessor((row: any) => row.advisorName || 'Unknown', {
      id: 'advisorName',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Advisor Name
          </SortHeader>,
        ),
      cell: info => <span className="text-sm">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Advisor Name' },
    }),
    seatColumnHelper.accessor((row: any) => row.advisorOrganization || 'Unknown', {
      id: 'advisorOrganization',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Organization
          </SortHeader>,
        ),
      cell: info => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Organization' },
    }),
    seatColumnHelper.accessor('status', {
      id: 'seatStatus',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Status
          </SortHeader>,
        ),
      cell: info => (
        <Badge variant={getSeatStatusBadgeVariant(info.getValue())}>{info.getValue()}</Badge>
      ),
      enableSorting: true,
      meta: { label: 'Status' },
    }),
    seatColumnHelper.accessor('createdDateTime', {
      id: 'seatCreatedDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Created Date
          </SortHeader>,
        ),
      cell: info => (
        <span className="text-sm">{new Date(info.getValue()).toLocaleDateString()}</span>
      ),
      enableSorting: true,
      meta: { label: 'Created Date' },
    }),
    seatColumnHelper.accessor('updatedDateTime', {
      id: 'seatUpdatedDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Updated Date
          </SortHeader>,
        ),
      cell: info => (
        <span className="text-sm">{new Date(info.getValue()).toLocaleDateString()}</span>
      ),
      enableSorting: true,
      meta: { label: 'Updated Date' },
    }),
  ];

  return (
    <div className="space-y-6">
      {/* Seats DataTable - Shown when subscription is selected */}
      {selectedSubscription &&
        selectedSubscription.seats &&
        selectedSubscription.seats.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Subscription Seats - {selectedSubscription.ownerAdvisorId}</span>
                <Button
                  onClick={() => setSelectedSubscription(null)}
                  variant="ghost"
                  size="sm"
                >
                  Close
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={seatColumnDefinitions as ColumnDef<AdvisorMetricSubscriptionSeats>[]}
                data={selectedSubscription.seats}
                tableId="subscription-seats-table"
                getRowId={row => `${row.advisorId}-${row.invitedEmail}`}
                isLoading={isLoading}
                variant="outlined"
                initialSorting={[{ id: 'seatCreatedDate', desc: true }]}
                pageSize={10}
                globalSearchText={seatSearchText}
                handleSearchTextChange={setSeatSearchText}
                globalSearchFieldKeys={['email', 'advisorId', 'seatStatus']}
                globalSearchPlaceholder="Search seats..."
                headerExtras={
                  <Button
                    onClick={exportSeatsToCsv}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export Seats CSV
                  </Button>
                }
              />
            </CardContent>
          </Card>
        )}

      {/* Subscriptions DataTable */}
      <DataTable
        columns={subscriptionColumnDefinitions as ColumnDef<AdvisorSubscriptionMetrics>[]}
        data={filteredSubscriptions}
        tableId="subscription-details-table"
        getRowId={row => row.ownerAdvisorId}
        isLoading={isLoading}
        variant="outlined"
        initialSorting={[{ id: 'createdDate', desc: true }]}
        pageSize={30}
        globalSearchText={searchText}
        handleSearchTextChange={setSearchText}
        globalSearchFieldKeys={['ownerAdvisorId', 'status', 'billingInterval']}
        globalSearchPlaceholder="Search subscriptions..."
        headerExtras={
          <div className="flex gap-2">
            {filterKey && (
              <>
                <Button
                  onClick={clearFilter}
                  variant="outline"
                  size="sm"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear Filter
                </Button>
                <Button
                  onClick={checkUsage}
                  variant="outline"
                  size="sm"
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Check Usage
                </Button>
              </>
            )}
            <Button
              onClick={exportSubscriptionsToCsv}
              variant="outline"
              size="sm"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        }
        isRowDeactivated={row =>
          selectedSubscription?.ownerAdvisorId === row.ownerAdvisorId ? true : false
        }
      />
    </div>
  );
}
